import type { TicketLineInput } from '../types/ticketCapture';

// Ticket Mixto: lógica PURA del verificador y de la vista previa de agrupación (sin React).
// Regla §7 del diseño: importes con precisión de centavos (jamás float suelto) y el mismatch se
// declara, nunca se adivina ni se prorratea.

export const toCents = (value: number): number => Math.round(value * 100);

export function sumTicketLinesCents(lines: TicketLineInput[]): number {
  return lines.reduce((sum, line) => sum + toCents(line.kind === 'INVENTORY' ? line.totalCost : line.amount), 0);
}

export interface TicketReconciliation {
  state: 'EMPTY' | 'MATCH' | 'MISMATCH_BLOCKED' | 'MISMATCH_DECLARED';
  linesTotal: number;
  message: string;
}

/** "Tus renglones suman $X / Ticket $Y" — bloquea el guardado salvo ajuste declarado. */
export function ticketReconciliation(lines: TicketLineInput[], physicalTotal: number, adjustmentNote: string): TicketReconciliation {
  const linesCents = sumTicketLinesCents(lines);
  const linesTotal = linesCents / 100;
  if (lines.length === 0) return { state: 'EMPTY', linesTotal: 0, message: 'Agrega los renglones del ticket.' };
  const totalCents = toCents(physicalTotal);
  if (linesCents === totalCents) return { state: 'MATCH', linesTotal, message: `Tus renglones suman $${linesTotal.toFixed(2)} / Ticket $${physicalTotal.toFixed(2)} ✓` };
  const base = `Tus renglones suman $${linesTotal.toFixed(2)} / Ticket $${physicalTotal.toFixed(2)}`;
  if (adjustmentNote.trim()) return { state: 'MISMATCH_DECLARED', linesTotal, message: `${base} — diferencia declarada: ${adjustmentNote.trim()}` };
  return { state: 'MISMATCH_BLOCKED', linesTotal, message: `${base}. Corrige los renglones o declara el ajuste/redondeo.` };
}

/** Cuántos documentos internos generará el guardado (informativo del resumen, no jerga obligatoria). */
export function ticketSplitPreview(lines: TicketLineInput[]): { inventoryCount: number; inventoryTotal: number; expenseGroups: number; expensesTotal: number } {
  const inventory = lines.filter((line) => line.kind === 'INVENTORY');
  const expenses = lines.filter((line) => line.kind === 'EXPENSE');
  const groups = new Set(expenses.map((line) => `${line.category}||${line.subcategory?.trim() || ''}`));
  return {
    inventoryCount: inventory.length,
    inventoryTotal: inventory.reduce((sum, line) => sum + toCents(line.totalCost), 0) / 100,
    expenseGroups: groups.size,
    expensesTotal: expenses.reduce((sum, line) => sum + toCents(line.amount), 0) / 100,
  };
}

// ── Pre-client UX hardening: renglones compactos (móvil primero) ────────────────────────────────
// Un ticket real trae 5–15 renglones; con cada renglón desplegado como formulario completo, un
// teléfono acumulaba miles de píxeles de scroll dentro del modal. Regla nueva de la vista: solo el
// renglón ACTIVO se expande; los demás se muestran compactos (qué es · clasificación · importe ·
// Editar). Esta función pura resume un renglón capturado para su fila compacta.

export interface TicketDraftLineSummaryInput {
  kind: 'INVENTORY' | 'EXPENSE';
  /** Descripción tecleada (gasto) o nota del renglón (insumo). */
  description: string;
  /** Nombre del insumo elegido (solo INVENTORY; null/'' si aún no elige). */
  ingredientName?: string | null;
  /** Etiqueta visible de la categoría (solo EXPENSE; null/'' si aún no elige). */
  categoryLabel?: string | null;
  quantityText?: string;
  unitSymbol?: string | null;
  /** Importe tecleado del renglón (totalCost del insumo o amount del gasto). */
  amountText: string;
}

export interface TicketDraftLineSummary {
  /** Qué se compró, en palabras del usuario. */
  title: string;
  /** Información secundaria mínima útil (cantidad·unidad o categoría). */
  secondary: string;
  /** Importe listo para mostrar ('$45.00') o '—' si aún no hay importe válido. */
  amountLabel: string;
  /** true si el renglón tiene todo lo necesario para guardarse (espejo de parsedLines). */
  complete: boolean;
}

export function ticketLineSummary(line: TicketDraftLineSummaryInput): TicketDraftLineSummary {
  const amount = Number(line.amountText);
  const amountValid = Number.isFinite(amount) && amount > 0;
  const amountLabel = amountValid ? `$${amount.toFixed(2)}` : '—';
  if (line.kind === 'INVENTORY') {
    const quantity = Number(line.quantityText);
    const quantityValid = Number.isFinite(quantity) && quantity > 0;
    const title = line.ingredientName?.trim() || 'Insumo sin elegir';
    const secondary = quantityValid ? `${line.quantityText} ${line.unitSymbol ?? ''}`.trim() : 'Falta la cantidad';
    return { title, secondary, amountLabel, complete: Boolean(line.ingredientName?.trim()) && quantityValid && amountValid };
  }
  const title = line.description.trim() || 'Gasto sin describir';
  const secondary = line.categoryLabel?.trim() || 'Falta la categoría';
  return { title, secondary, amountLabel, complete: line.description.trim() !== '' && Boolean(line.categoryLabel?.trim()) && amountValid };
}
